import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, type Me } from "../api/client";
import { applyTheme, localTheme, themeOf } from "./theme";

interface Session {
  me: Me | null;
  setMe: (m: Me | null) => void;
  /** Bumped whenever term state or the profile changes; screens depend on it to reload. */
  version: number;
  bump: () => void;
  providers: string[];
  message: string | null;
  toast: (text: string) => void;
  signInOpen: boolean;
  openSignIn: () => void;
  closeSignIn: () => void;
  pushAskOpen: boolean;
  openPushAsk: () => void;
  closePushAsk: () => void;
  /** True when signed in; otherwise opens the sign-in sheet and returns false. */
  requireAuth: () => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

const TOAST_MS = 4500;

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [version, setVersion] = useState(0);
  const [providers, setProviders] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [pushAskOpen, setPushAskOpen] = useState(false);
  const toastTimer = useRef<number | undefined>(undefined);
  const tzReported = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setMe(await api.me());
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null));
    api
      .providers()
      .then((r) => setProviders(r.providers))
      .catch(() => {});
  }, []);

  // Quiet hours run in the reader's own time zone; the browser reports it once, when the
  // server does not have one yet.
  useEffect(() => {
    if (!me || me.timezone || tzReported.current === me.id) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    tzReported.current = me.id;
    api
      .patchMe({ timezone: tz })
      .then(setMe)
      .catch(() => {});
  }, [me]);

  // The account's theme wins while signed in; the device choice applies otherwise.
  useEffect(() => {
    applyTheme(me ? themeOf(me.theme) : localTheme());
  }, [me]);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const toast = useCallback((text: string) => {
    window.clearTimeout(toastTimer.current);
    setMessage(text);
    toastTimer.current = window.setTimeout(() => setMessage(null), TOAST_MS);
  }, []);

  const openSignIn = useCallback(() => setSignInOpen(true), []);
  const closeSignIn = useCallback(() => setSignInOpen(false), []);
  const openPushAsk = useCallback(() => setPushAskOpen(true), []);
  const closePushAsk = useCallback(() => setPushAskOpen(false), []);

  const requireAuth = useCallback(() => {
    if (me) return true;
    setSignInOpen(true);
    return false;
  }, [me]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setMe(null);
      bump();
      // The offline story cache is personalised; it does not outlive the session.
      try {
        await caches.delete("api-read");
      } catch {
        /* no Cache API: nothing to clear */
      }
    }
  }, [bump]);

  const value: Session = {
    me,
    setMe,
    version,
    bump,
    providers,
    message,
    toast,
    signInOpen,
    openSignIn,
    closeSignIn,
    pushAskOpen,
    openPushAsk,
    closePushAsk,
    requireAuth,
    refresh,
    signOut,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession used outside SessionProvider");
  return ctx;
}
